import React, { useState } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Search, Calendar, Clock, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { getBookingsByPhone } from "../lib/api";

export default function MyBookings() {
  const [phone, setPhone] = useState("");
  const [bookings, setBookings] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    if (!phone.trim()) {
      toast.error("Enter a phone number");
      return;
    }
    setLoading(true);
    try {
      const { data } = await getBookingsByPhone(phone.trim());
      setBookings(data);
      setSearched(true);
    } catch (err) {
      toast.error("Could not load bookings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="my-bookings-page" className="min-h-screen bg-[#0A0A0A] pt-24 pb-20">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <p className="text-xs uppercase tracking-[0.3em] text-[#CCFF00] mb-3">My Bookings</p>
        <h1 className="font-display text-5xl lg:text-7xl uppercase tracking-tighter leading-none">
          Track your slots
        </h1>
        <p className="text-white/60 mt-4 max-w-lg">
          Enter the phone number you booked with to see all your reservations.
        </p>

        <form onSubmit={search} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-xl">
          <Input
            data-testid="mybookings-phone-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Enter your phone number"
            className="bg-[#121212] border-white/15 rounded-none text-white focus:border-[#CCFF00] h-12"
          />
          <Button
            data-testid="mybookings-search-btn"
            type="submit"
            disabled={loading}
            className="rounded-none bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#D4FF33] h-12 px-6"
          >
            <Search className="w-4 h-4 mr-2" strokeWidth={2} />
            {loading ? "Searching..." : "Look up"}
          </Button>
        </form>

        <div className="mt-12">
          {searched && bookings.length === 0 && (
            <div data-testid="no-bookings" className="text-center py-16 border border-dashed border-white/15">
              <p className="text-white/40 uppercase tracking-[0.3em] text-xs">
                No bookings found for this number.
              </p>
            </div>
          )}

          <div className="space-y-3" data-testid="my-bookings-list">
            {bookings.map((b) => (
              <div
                key={b.id}
                data-testid={`booking-${b.id}`}
                className="bg-[#121212] border border-white/10 hover:border-[#CCFF00]/40 transition-colors p-6 grid sm:grid-cols-12 gap-4 items-center"
              >
                <div className="sm:col-span-3">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Date</div>
                  <div className="font-display text-2xl uppercase mt-1 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#CCFF00]" strokeWidth={1.8} />
                    {b.date}
                  </div>
                </div>
                <div className="sm:col-span-3">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Time</div>
                  <div className="font-display text-2xl uppercase mt-1 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#CCFF00]" strokeWidth={1.8} />
                    {b.hour_label}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Price</div>
                  <div className="font-display text-2xl mt-1">₹{b.price}</div>
                </div>
                <div className="sm:col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Status</div>
                  <div className="mt-1">
                    {b.status === "confirmed" ? (
                      <Badge className="bg-[#CCFF00] text-black uppercase tracking-widest rounded-none hover:bg-[#CCFF00]">
                        Confirmed
                      </Badge>
                    ) : (
                      <Badge className="bg-[#FF3B30] text-white uppercase tracking-widest rounded-none hover:bg-[#FF3B30]">
                        Cancelled
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">ID</div>
                  <div className="text-xs font-mono text-white/60 mt-1 truncate">{b.id.slice(0, 8)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
